if (!customElements.get('quick-add-modal')) {
  customElements.define('quick-add-modal', class QuickAddModal extends ModalDialog {
    constructor() {
      super();
      this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
    }

    hide(preventFocus = false) {
      const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      if (cartNotification) cartNotification.setActiveElement(this.openedBy);
      if (this.unavailableOptionsObserver) {
        this.unavailableOptionsObserver.disconnect();
        this.unavailableOptionsObserver = null;
      }
      this.modalContent.innerHTML = '';

      if (preventFocus) this.openedBy = null;
      super.hide();
    }

    show(opener) {
      if (this.loading) return;
      this.loading = true;
      this.quickProductUrl = opener.getAttribute('data-product-url');
      this.productElement = null;
      const dialog = this.querySelector('[role="dialog"]');
      if (dialog && dialog.getAttribute('aria-describedby') === `${this.id}-load-error`) {
        dialog.removeAttribute('aria-describedby');
      }
      const spinner = opener.querySelector('.loading-overlay__spinner');
      opener.setAttribute('aria-disabled', true);
      opener.classList.add('loading');
      if (spinner) spinner.classList.remove('hidden');

      return fetch(this.quickProductUrl)
        .then((response) => {
          if (!response.ok) throw new Error('Quick options request failed');
          return response.text();
        })
        .then((responseText) => {
          const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
          this.productElement = responseHTML.querySelector('section[id^="MainProduct-"]');
          if (!this.productElement || !this.productElement.dataset.section || !this.productElement.dataset.section.trim() ||
              !this.productElement.querySelector('.product') ||
              !this.productElement.querySelector('.product__info-container')) {
            throw new Error('Invalid quick options response');
          }

          // ZYPHER V223 QUICK ADD NAVIGATION GUARD
          // Full product pages contain a cheapest-variant redirect script.
          // Never execute page-navigation scripts inside the Quick Add modal.
          this.productElement
            .querySelectorAll('script[data-zypher-page-variant-redirect]')
            .forEach((script) => script.remove());

          this.preventDuplicatedIDs();
          this.removeDOMElements();
          this.simplifyForZypherQuickOptions();
          this.setInnerHTML(this.modalContent, this.productElement.innerHTML);
          this.replaceZypherQuickMedia();
          this.decorateZypherQuickOptions();
          this.syncUnavailableOptionSemantics();

          if (window.Shopify && Shopify.PaymentButton) {
            Shopify.PaymentButton.init();
          }

          if (window.ProductModel) window.ProductModel.loadShopifyXR();

          this.removeGalleryListSemantic();
          this.updateImageSizes();
          this.preventVariantURLSwitching();
          super.show(opener);
        })
        .catch(() => {
          this.showLoadError(opener);
        })
        .finally(() => {
          this.loading = false;
          opener.removeAttribute('aria-disabled');
          opener.classList.remove('loading');
          if (spinner) spinner.classList.add('hidden');
        });
    }

    showLoadError(opener) {
      this.productElement = null;
      const content = document.createElement('div');
      content.className = 'product__info-container';
      const title = document.createElement('h2');
      title.className = 'zypher-quick-options-title';
      title.textContent = 'Quick options unavailable';
      const message = document.createElement('p');
      message.id = `${this.id}-load-error`;
      message.setAttribute('role', 'alert');
      message.textContent = 'We couldn’t load options here. View the full product page to see available options.';
      content.append(title, message);
      if (this.quickProductUrl) {
        const link = document.createElement('a');
        link.className = 'zypher-quick-options-view-full';
        link.href = this.quickProductUrl;
        link.textContent = 'View full product details';
        content.appendChild(link);
      }
      this.modalContent.replaceChildren(content);
      const dialog = this.querySelector('[role="dialog"]');
      if (dialog) dialog.setAttribute('aria-describedby', message.id);
      super.show(opener);
    }

    setInnerHTML(element, html) {
      element.innerHTML = html;

      // Reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
      element.querySelectorAll('script').forEach(oldScriptTag => {
        const newScriptTag = document.createElement('script');
        Array.from(oldScriptTag.attributes).forEach(attribute => {
          newScriptTag.setAttribute(attribute.name, attribute.value)
        });
        newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
        oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
      });
    }

    simplifyForZypherQuickOptions() {
      if (!this.productElement) return;

      this.productElement.querySelectorAll(
        '.product__media-item:not(:first-child), .thumbnail-slider, .thumbnail-list, ' +
        '.product__description, .product__accordion, .accordion, share-button, .share-button, ' +
        'complementary-products, .product__popup-modal, pickup-availability'
      ).forEach((node) => node.remove());

      const info = this.productElement.querySelector('.product__info-container');
      if (info) {
        Array.from(info.children).forEach((child) => {
          const keep =
            child.matches('.product__title') ||
            child.matches('[id^="price-"]') ||
            child.matches('variant-selects, variant-radios') ||
            child.matches('.product-form__quantity') ||
            child.matches('product-form') ||
            child.querySelector('product-form') !== null ||
            child.matches('.product__view-details') ||
            child.matches('.product__inventory') ||
            child.matches('script, noscript');

          if (!keep) child.remove();
        });
        this.consolidateZypherQuickOptions(info);
      }
    }

    consolidateZypherQuickOptions(info) {
      const product = this.productElement.querySelector('.product');
      if (product) product.classList.add('zypher-quick-options-product');
      info.classList.add('zypher-quick-options-info');

      const purchase = info.querySelector('product-form');
      if (!purchase) return;
      let purchaseWrapper = purchase;
      while (purchaseWrapper.parentElement && purchaseWrapper.parentElement !== info) {
        purchaseWrapper = purchaseWrapper.parentElement;
      }
      if (purchaseWrapper.parentElement !== info) return;
      purchaseWrapper.classList.add('zypher-quick-options-buy');

      const quantity = info.querySelector('.product-form__quantity');
      if (!quantity || quantity.parentElement !== info) return;
      const promo = quantity.querySelector('.z167-quantity-promo');
      const row = document.createElement('div');
      row.className = 'zypher-quick-options-purchase';
      info.insertBefore(row, quantity);
      row.append(quantity, purchaseWrapper);
      if (promo) {
        promo.classList.add('zypher-quick-options-promo');
        row.insertAdjacentElement('afterend', promo);
      }
    }

    replaceZypherQuickMedia() {
      const wrapper = this.modalContent.querySelector('.product__media-wrapper');
      if (!wrapper) return;

      const source = wrapper.querySelector('img');
      if (!source) {
        wrapper.remove();
        return;
      }

      const shell = document.createElement('div');
      shell.className = 'zypher-quick-options-media';

      const image = source.cloneNode(true);
      image.removeAttribute('style');
      image.loading = 'eager';
      image.decoding = 'async';
      image.setAttribute('sizes','(min-width: 750px) 260px, calc(100vw - 64px)');

      shell.appendChild(image);
      wrapper.replaceChildren(shell);
    }

    decorateZypherQuickOptions() {
      const info = this.modalContent.querySelector('.product__info-container');
      if (!info) return;

      if (!info.querySelector('.zypher-quick-options-kicker')) {
        const kicker = document.createElement('div');
        kicker.className = 'zypher-quick-options-kicker';
        kicker.textContent = 'Quick Options';
        info.insertBefore(kicker, info.firstChild);
      }

      const originalTitle = info.querySelector('.product__title');
      if (originalTitle && !info.querySelector('.zypher-quick-options-title')) {
        const sourceTitle = originalTitle.querySelector('h1,h2,a');
        const sourceLink = originalTitle.querySelector('a[href]');
        const title = document.createElement(sourceLink ? 'a' : 'div');
        title.className = 'zypher-quick-options-title';
        const titleText = sourceTitle ? sourceTitle.textContent.trim() : '';
        const separator = titleText.lastIndexOf(' | ');
        title.textContent = separator > 0 ? titleText.slice(0, separator) : titleText;
        if (sourceLink) title.href = sourceLink.href;
        originalTitle.insertAdjacentElement('afterend', title);
        if (separator > 0) {
          const platform = document.createElement('div');
          platform.className = 'zypher-quick-options-platform';
          platform.textContent = titleText.slice(separator + 3);
          title.insertAdjacentElement('afterend', platform);
        }
        originalTitle.remove();
      }

      info.querySelectorAll('.product__view-details').forEach((node) => node.remove());

      if (!info.querySelector('.zypher-quick-options-view-full') && this.quickProductUrl) {
        const full = document.createElement('a');
        full.className = 'zypher-quick-options-view-full';
        full.href = this.quickProductUrl;
        full.textContent = 'View full product details →';

        info.appendChild(full);
      }
    }

    syncUnavailableOptionSemantics() {
      if (this.unavailableOptionsObserver) this.unavailableOptionsObserver.disconnect();
      const picker = this.modalContent.querySelector('variant-radios');
      if (!picker) return;

      const sync = () => {
        picker.querySelectorAll('input[type="radio"]').forEach((input) => {
          if (input.disabled || input.classList.contains('disabled')) {
            input.setAttribute('aria-disabled', 'true');
          } else {
            input.removeAttribute('aria-disabled');
          }
        });
      };

      sync();
      this.unavailableOptionsObserver = new MutationObserver(sync);
      this.unavailableOptionsObserver.observe(picker, {
        attributes: true,
        attributeFilter: ['class', 'disabled'],
        childList: true,
        subtree: true
      });
    }

    preventVariantURLSwitching() {
      const variantPicker = this.modalContent.querySelector('variant-radios,variant-selects');
      if (!variantPicker) return;

      variantPicker.setAttribute('data-update-url', 'false');
    }

    removeDOMElements() {
      const pickupAvailability = this.productElement.querySelector('pickup-availability');
      if (pickupAvailability) pickupAvailability.remove();

      const productModal = this.productElement.querySelector('product-modal');
      if (productModal) productModal.remove();

      const modalDialog = this.productElement.querySelectorAll('modal-dialog');
      if (modalDialog) modalDialog.forEach(modal => modal.remove());
    }

    preventDuplicatedIDs() {
      const sectionId = this.productElement.dataset.section;
      this.productElement.innerHTML = this.productElement.innerHTML.replaceAll(sectionId, `quickadd-${ sectionId }`);
      this.productElement.querySelectorAll('variant-selects, variant-radios, product-info').forEach((element) => {
        element.dataset.originalSection = sectionId;
      });
    }

    removeGalleryListSemantic() {
      const galleryList = this.modalContent.querySelector('[id^="Slider-Gallery"]');
      if (!galleryList) return;

      galleryList.setAttribute('role', 'presentation');
      galleryList.querySelectorAll('[id^="Slide-"]').forEach(li => li.setAttribute('role', 'presentation'));
    }

    updateImageSizes() {
      const product = this.modalContent.querySelector('.product');
      const desktopColumns = product.classList.contains('product--columns');
      if (!desktopColumns) return;

      const mediaImages = product.querySelectorAll('.product__media img');
      if (!mediaImages.length) return;

      let mediaImageSizes = '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';
      
      if (product.classList.contains('product--medium')) {
        mediaImageSizes = mediaImageSizes.replace('715px', '605px');
      } else if (product.classList.contains('product--small')) {
        mediaImageSizes = mediaImageSizes.replace('715px', '495px');
      }

      mediaImages.forEach(img => img.setAttribute('sizes', mediaImageSizes));
    }
  });
}
